// src/pages/clients/ClientEquipmentMap.js
import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    Chip
} from '@mui/material';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIconShadow from 'leaflet/dist/images/marker-shadow.png';

// Corregir el problema de los iconos en Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: markerIcon,
    shadowUrl: markerIconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

// Crear iconos personalizados según estado
const createStatusIcon = (status) => {
    const color = status === 'active' ? '#10b981' : // Verde esmeralda
        status === 'returned' ? '#fbbf24' : // Amarillo ámbar
        status === 'lost' ? '#ef4444' : '#3f51b5'; // Rojo o Azul

    const svgPin = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="36">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="${color}" stroke="#ffffff" stroke-width="1.5"/>
        </svg>
    `;

    return L.divIcon({
        className: 'custom-gps-pin-icon',
        html: `<div style="display: flex; justify-content: center; align-items: center; filter: drop-shadow(0px 3px 5px rgba(0,0,0,0.35));">${svgPin}</div>`,
        iconSize: [28, 36],
        iconAnchor: [14, 36],
        popupAnchor: [0, -36]
    });
};

const ClientEquipmentMap = () => {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markersLayerRef = useRef(null);
    const mapInitializedRef = useRef(false);

    // Inicializar zones como un array vacío para evitar el error zones.map
    const [zones, setZones] = useState([]);
    const [selectedZone, setSelectedZone] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [equipmentCount, setEquipmentCount] = useState({
        total: 0,
        active: 0,
        returned: 0
    });

    // Cargar zonas al inicio
    useEffect(() => {
        fetchZones();
    }, []);

    // Inicializar mapa solo una vez
    useEffect(() => {
        if (!mapRef.current || mapInitializedRef.current) return;

        initializeMap();

        // Limpiar al desmontar
        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
                mapInitializedRef.current = false;
            }
        };
    }, []);

    // Cargar datos cuando cambian los filtros
    useEffect(() => {
        if (mapInstanceRef.current && markersLayerRef.current) {
            loadEquipment();
        }
    }, [selectedZone, statusFilter]);

    const fetchZones = async () => {
        try {
            const response = await fetch('/api/zones', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) {
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();

            // Verificar que data es un array antes de asignarlo
            if (Array.isArray(data)) {
                setZones(data);
            } else {
                console.error('La respuesta de /api/zones no es un array:', data);
                setZones([]); // Establecer un array vacío en caso de respuesta inesperada
            }
        } catch (error) {
            console.error('Error fetching zones:', error);
            setZones([]); // Garantizar que siempre sea un array en caso de error
        }
    };

    const initializeMap = () => {
        if (!mapRef.current || mapInitializedRef.current) return;

        try {
            const osm = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
                maxZoom: 22,
                maxNativeZoom: 19
            });

            const googleHybrid = L.tileLayer('https://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}', {
                subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
                attribution: '&copy; Google Maps',
                maxZoom: 22,
                maxNativeZoom: 20
            });

            const esriSatellite = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
                attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
                maxZoom: 22,
                maxNativeZoom: 17
            });

            // Crear instancia de mapa
            const map = L.map(mapRef.current, {
                center: [-4.9, -80.77], // Aproximadamente Perú (ajustar según ubicación)
                zoom: 13,
                maxZoom: 22,
                layers: [googleHybrid]
            });

            const baseLayers = {
                "Mapa Estándar": osm,
                "Satelital Google (Híbrido)": googleHybrid,
                "Satélite Esri": esriSatellite
            };

            L.control.layers(baseLayers).addTo(map);

            // Guardar referencia del mapa y crear capa para marcadores
            mapInstanceRef.current = map;
            markersLayerRef.current = L.layerGroup().addTo(map);
            mapInitializedRef.current = true;

            // Añadir controles adicionales
            L.control.scale().addTo(map);

            // Cargar datos iniciales
            loadEquipment();
        } catch (error) {
            console.error("Error initializing map:", error);
            setError("Error al inicializar el mapa: " + error.message);
            setLoading(false);
        }
    };

    const loadEquipment = async () => {
        if (!mapInstanceRef.current || !markersLayerRef.current) return;

        try {
            setLoading(true);
            setError(null);

            // Limpiar marcadores existentes
            markersLayerRef.current.clearLayers();

            // Construir URL con parámetros de consulta
            let url = '/client-equipment/map';
            const params = new URLSearchParams();

            if (selectedZone) {
                params.append('zone', selectedZone);
            }

            if (statusFilter !== 'all') {
                params.append('status', statusFilter);
            }

            if (params.toString()) {
                url += `?${params.toString()}`;
            }

            // Realizar petición
            const response = await fetch(`/api${url}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) {
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();

            // Verificar que data es un array antes de procesarlo
            if (!Array.isArray(data)) {
                console.error('La respuesta no es un array:', data);
                throw new Error('La respuesta del servidor no tiene el formato esperado');
            }

            // Verificar si hay datos
            if (data.length === 0) {
                setEquipmentCount({ total: 0, active: 0, returned: 0 });
                setError("No se encontraron equipos para mostrar en el mapa");
                setLoading(false);
                return;
            }

            // Contar por estado
            const counts = {
                total: data.length,
                active: 0,
                returned: 0,
                lost: 0
            };

            // Para verificar si hay marcadores válidos
            let hasValidMarkers = false;
            // Array para almacenar coordenadas válidas
            const validCoords = [];

            // Procesar equipos
            data.forEach(item => {
                // Contar por estado
                counts[item.status] = (counts[item.status] || 0) + 1;

                // Verificar y procesar coordenadas
                if (item.coordinates) {
                    // Parsear coordenadas
                    const coordParts = item.coordinates.split(',');

                    if (coordParts.length === 2) {
                        const lat = parseFloat(coordParts[0].trim());
                        const lng = parseFloat(coordParts[1].trim());

                        if (!isNaN(lat) && !isNaN(lng)) {
                            // Crear icono según estado
                            const icon = createStatusIcon(item.status);

                            // Crear marcador
                            const marker = L.marker([lat, lng], { icon })
                                .addTo(markersLayerRef.current)
                                .bindPopup(`
                                    <div>
                                        <h3>${item.client_name || 'Cliente sin nombre'}</h3>
                                        <p><strong>Equipo:</strong> ${item.item_name || 'N/A'}</p>
                                        <p><strong>Modelo:</strong> ${item.model || 'N/A'}</p>
                                        <p><strong>Serial:</strong> ${item.serial_number || 'N/A'}</p>
                                        <p><strong>Estado:</strong> ${item.status === 'active' ? 'Activo' :
                                        item.status === 'returned' ? 'Devuelto' :
                                            item.status === 'lost' ? 'Perdido' : item.status
                                    }</p>
                                        <p><strong>Dirección:</strong> ${item.address || 'N/A'}</p>
                                    </div>
                                `);

                            // Indicar que tenemos al menos un marcador válido
                            hasValidMarkers = true;
                            // Guardar las coordenadas válidas
                            validCoords.push([lat, lng]);
                        }
                    }
                }
            });

            // Actualizar contadores
            setEquipmentCount(counts);

            // Ajustar la vista si hay marcadores válidos
            if (hasValidMarkers && validCoords.length > 0) {
                // Crear un FeatureGroup con todos los marcadores
                const group = L.featureGroup(markersLayerRef.current.getLayers());
                // Ajustar la vista a los límites del grupo
                mapInstanceRef.current.fitBounds(group.getBounds(), { padding: [30, 30] });
            } else if (data.length > 0) {
                // Si hay equipos pero sin coordenadas válidas
                setError("No se pudieron mostrar los equipos en el mapa por falta de coordenadas válidas");
            }

        } catch (error) {
            console.error("Error loading equipment:", error);
            setError("Error al cargar equipos: " + (error.message || "Error desconocido"));
        } finally {
            setLoading(false);
        }
    };

    const handleZoneChange = (e) => {
        setSelectedZone(e.target.value);
    };

    const handleStatusChange = (e) => {
        setStatusFilter(e.target.value);
    };

    return (
        <Box sx={{ 
            p: { xs: 1.5, md: 2 }, 
            height: { xs: 'auto', md: 'calc(100vh - 90px)' }, 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 2, 
            overflow: { xs: 'auto', md: 'hidden' } 
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, flexShrink: 0 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.65rem' } }}>
                        Mapa de Equipos de Clientes
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.2, fontSize: '12.5px' }}>
                        Ubicación geográfica de los equipos CPE y router instalados en los clientes.
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Estado de Equipo</InputLabel>
                    <Select
                        value={statusFilter}
                        onChange={handleStatusChange}
                        label="Estado de Equipo"
                        sx={{ borderRadius: 2 }}
                    >
                        <MenuItem value="all">Todos los estados</MenuItem>
                        <MenuItem value="active">Activos</MenuItem>
                        <MenuItem value="returned">Devueltos</MenuItem>
                        <MenuItem value="lost">Perdidos</MenuItem>
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Zona</InputLabel>
                    <Select
                        value={selectedZone}
                        onChange={handleZoneChange}
                        label="Zona"
                        sx={{ borderRadius: 2 }}
                    >
                        <MenuItem value="">Todas las zonas</MenuItem>
                        {/* Verificar que zones sea un array antes de usar map */}
                        {Array.isArray(zones) ?
                            zones.map(zone => (
                                <MenuItem key={zone.id} value={zone.id}>{zone.name}</MenuItem>
                              )) :
                            <MenuItem value="">Error al cargar zonas</MenuItem>
                        }
                    </Select>
                </FormControl>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                        label={`Activos: ${equipmentCount.active || 0}`}
                        color="success"
                        variant="outlined"
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                    />
                    <Chip
                        label={`Total: ${equipmentCount.total || 0}`}
                        color="primary"
                        variant="outlined"
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                    />
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ borderRadius: 2, py: 0.5 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Paper 
                elevation={0} 
                sx={{ 
                    position: 'relative', 
                    flex: 1, 
                    minHeight: 0,
                    borderRadius: 3, 
                    border: '1px solid #e2e8f0', 
                    overflow: 'hidden',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.04)'
                }}
            >
                <div
                    ref={mapRef}
                    style={{ height: '100%', width: '100%' }}
                ></div>

                {loading && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            zIndex: 999
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default ClientEquipmentMap;